import { Button, Checkbox } from '@any-ui-react/core';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { v4 as uuid } from 'uuid';

import { ErrorCard } from '@/common/components/shared/error-card';
import { GoogleLogoIcon } from '@/common/components/shared/icons';
import { useAuthContext } from '@/common/contexts';
import { useGenerateSignInOAuthUrlQuery } from '@/common/graphql';
import {
  AppEnvironment,
  AUTH_PKCE_VERIFIER_KEY,
  AUTH_STATE_KEY,
  DateUtils,
  deriveCodeChallenge,
  generateCodeVerifier,
  getRememberedSignInAccount,
  isSharedComputer,
  replaceSessionStorageItem,
  SIGN_IN_ERROR_TITLE,
  setSharedComputer,
  setStorageItem,
} from '@/common/utils';

export const Login = () => {
  const { authUser } = useAuthContext();
  const authState = setStorageItem(AUTH_STATE_KEY, uuid()) ?? '';
  const [codeChallenge, setCodeChallenge] = useState<string>();
  const [isPKCEUnavailable, setPKCEUnavailable] = useState(false);
  const [isShared, setShared] = useState(isSharedComputer);
  // Signs in to the last signed-in account without Google's chooser, unless the computer is shared. Read from storage on
  // every render (a single localStorage read), so it always reflects the latest checkbox change.
  const loginHint = isShared ? null : getRememberedSignInAccount();

  // Checking the box clears the remembered account at once (setSharedComputer), so unchecking it again does not bring
  // the hint back.
  const onSharedChange = (checked: boolean) => {
    setShared(checked);
    setSharedComputer(checked);
  };

  // Each visit gets a fresh verifier for this tab; the URL behind the button is only requested with its challenge.
  // There is no fallback to a sign-in without PKCE.
  useEffect(() => {
    let isCancelled = false;

    const preparePKCE = async () => {
      try {
        const verifier = generateCodeVerifier();
        // Without a stored verifier the callback could not complete the sign-in, so treat it like missing crypto.
        if (!replaceSessionStorageItem(AUTH_PKCE_VERIFIER_KEY, verifier)) {
          throw new Error('sessionStorage is not available');
        }
        const challenge = await deriveCodeChallenge(verifier);
        if (!isCancelled) {
          setCodeChallenge(challenge);
        }
      } catch (_error) {
        if (!isCancelled) {
          setPKCEUnavailable(true);
        }
      }
    };

    void preparePKCE();

    return () => {
      isCancelled = true;
    };
  }, []);

  const isTokenValid = useCallback(() => {
    try {
      const minuteDifference = DateUtils.getMinuteDifference(new Date(), new Date(authUser.exp * 1000));
      return minuteDifference >= 0;
    } catch (_e) {
      return false;
    }
  }, [authUser.exp]);

  const { data, loading } = useGenerateSignInOAuthUrlQuery({
    variables: {
      state: authState,
      redirectUri:
        import.meta.env.VITE_ENVIRONMENT === AppEnvironment.LOCAL ? import.meta.env.VITE_AUTH_CALLBACK_URL : undefined,
      codeChallenge: codeChallenge ?? null,
      loginHint,
    },
    skip: !codeChallenge,
  });

  if (authUser && isTokenValid()) {
    return <Navigate to="/" />;
  }

  if (isPKCEUnavailable) {
    return (
      <ErrorCard
        guideline="Sign-in cannot start in this browser. Open MED over HTTPS, then try again."
        title={SIGN_IN_ERROR_TITLE}
      />
    );
  }

  return (
    <section className="flex justify-center rounded-lg bg-white shadow-card">
      <div className="flex shrink-0 grow flex-col justify-evenly gap-y-3 px-16 py-8">
        <div className="flex justify-center">
          <img alt="anymind-logo" height="50" src="/anymind-logo.svg" width="189" />
        </div>
        <div>
          <p className="text-center text-sm">Sign in with your company email address</p>
        </div>
        <div className="flex justify-center">
          <Checkbox
            checked={isShared}
            label="This is a shared computer"
            onChange={event => onSharedChange(event.currentTarget.checked)}
          />
        </div>
        <div className="text-center">
          {data?.generateSignInOAuthUrl ? (
            <Link to={data.generateSignInOAuthUrl}>
              <Button className="h-11" loading={loading} variant="default">
                <GoogleLogoIcon className="mr-1" />
                <span>Sign in with {loginHint ?? 'Google'}</span>
              </Button>
            </Link>
          ) : (
            <Button className="h-11" loading={true} variant="default">
              <GoogleLogoIcon className="mr-1" />
              <span>Sign in with Google</span>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
